import type { FileRejections } from "@/features/products/utils";

import * as React from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useErrorToast } from "@editor/hooks/useErrorToast";
import { analytics, trackError } from "@editor/infra/analytics";

import { useUploadAsset } from "@/features/assets/useUploadAsset";
import { handleFileRejections } from "@/features/products/utils";
import { useDropzone } from "react-dropzone";
import { MAX_FILE_UPLOAD_COUNT } from "schemas/asset";
import { v4 as uuidv4 } from "uuid";

import { sizeValidator } from "./utils/sizeValidator";

type CommonProps = {
  onDropBeforeUpload?: () => string | null;
  onUploadComplete: ({
    url,
    imageComponentId,
    file,
    id,
  }: {
    url: string;
    imageComponentId: string | null;
    file?: File;
    id?: string;
  }) => void;
  onError?: (imageComponentId: string | null) => void;
  acceptDropAssetType: string[] | null | undefined;
  allowClickToUpload: boolean;
  isDisabled?: boolean;
};

export function useReploFileDropZone(props: CommonProps) {
  const { uploadAsset, isUploading } = useUploadAsset();
  const result = useFileDropZone({
    ...props,
    uploadFile: async ({ id, file }) => {
      const url = await uploadAsset({ assetId: id, file });
      return url;
    },
  });
  return { ...result, isUploading };
}

function useFileDropZone({
  onDropBeforeUpload,
  onUploadComplete,
  onError,
  acceptDropAssetType,
  allowClickToUpload,
  isDisabled = false,
  uploadFile,
}: CommonProps & {
  uploadFile: (args: { file: File; id: string }) => Promise<string>;
}) {
  const [droppedFilesMap, setDroppedFilesMap] = React.useState<
    Map<string, File>
  >(new Map());
  const projectId = useCurrentProjectId();

  const errorToast = useErrorToast();

  const handleError = React.useCallback(
    ({
      id,
      imageComponentId,
    }: {
      id: string;
      imageComponentId?: string | null;
    }) => {
      setDroppedFilesMap((prev) => {
        const newMap = new Map(prev);
        newMap.delete(id);
        return newMap;
      });
      onError?.(imageComponentId ?? null);
    },
    [onError],
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open: openFileDialog,
    acceptedFiles,
  } = useDropzone({
    onDrop: (acceptedFiles: File[], fileRejections: FileRejections) => {
      const shouldStopProcessing = handleFileRejections(
        fileRejections,
        errorToast,
      );
      if (shouldStopProcessing) {
        return;
      }

      const internalDroppedFilesMap = new Map(
        acceptedFiles.map((file) => [uuidv4(), file]),
      );
      setDroppedFilesMap(internalDroppedFilesMap);

      const uploadPromises = Array.from(internalDroppedFilesMap.entries()).map(
        async ([id, file]) => {
          const imageComponentId = onDropBeforeUpload?.();
          try {
            const url = await uploadFile({ file, id });
            onUploadComplete({
              url,
              imageComponentId: imageComponentId ?? null,
              file,
              id,
            });
            analytics.logEvent("asset.upload.success", {
              projectId: projectId!,
              assetId: id,
              contentType: file.type,
              publicUrl: url,
            });
          } catch (error) {
            const extras = {
              projectId: projectId!,
              fileName: file.name,
              contentType: file.type,
              size: file.size,
            };
            trackError(error, extras);
            analytics.logEvent("asset.upload.failed", {
              error,
              ...extras,
            });
            errorToast(
              "Error Uploading Asset",
              "An error occurred while uploading the asset. Please try again.",
              "error.asset.upload.unknown",
              {},
            );
            handleError({ id, imageComponentId });
          }
        },
      );

      // NOTE (Sebas, 2025-01-31): We cannot await the uploadPromises because the onDrop
      // function is called synchronously and the type does not allow for async.
      void Promise.all(uploadPromises);
    },
    accept: acceptDropAssetType ?? undefined,
    maxFiles: MAX_FILE_UPLOAD_COUNT,
    noClick: !allowClickToUpload,
    noKeyboard: true,
    validator: sizeValidator,
    disabled: isDisabled,
  });

  return {
    getRootProps,
    getInputProps,
    openFileDialog,
    isDragActive,
    droppedFilesMap,
    acceptedFiles,
  };
}
